import type { AxiosPromise } from 'axios';

import type {
  TCatalogExportDownloadResult,
  TCatalogExportSubmitResponse,
  TCatalogFilterOptionsParams,
  TCatalogFilterOptionsResponse,
  TCatalogImportStatus,
  TCatalogImportSubmitResponse,
  TCatalogItem,
  TCatalogListResponse,
  TCreateCatalogItem,
  TGetCatalogItems,
  TGetCatalogItemsResponse,
  TPatchCatalogItem,
  TStartCatalogExportParams,
} from '@/types/productCatalog';

import { catalogService } from '@/api/private';

/**
 * Combined `filter` JSON for catalog requests: `{"stationId":[...],"category":[...],"shelfLocation":[...]}`.
 * Only non-empty arrays are included. No arrays gives no param.
 * Why: the backend reads all three from one `filter` JSON, not as separate repeated keys.
 */
const toCatalogFilterParam = (
  stationIds?: string[],
  categories?: string[],
  shelfLocations?: string[],
): { filter?: string } => {
  const filterObj: Record<string, string[]> = {};

  if (stationIds?.length) {
    filterObj.stationId = stationIds;
  }

  if (categories?.length) {
    filterObj.category = categories;
  }

  if (shelfLocations?.length) {
    filterObj.shelfLocation = shelfLocations;
  }

  return Object.keys(filterObj).length > 0 ? { filter: JSON.stringify(filterObj) } : {};
};

const EMPTY_LIST_RESPONSE: TGetCatalogItemsResponse = {
  message: '',
  data: {
    hasMore: false,
    result: [],
    totalHits: 0,
    total: 0,
  },
};

const POLL_INTERVAL_MS = 2000;

type TCatalogQueryValue = string | number | boolean | string[] | undefined;

/**
 * Query serializer for every catalog request.
 *
 * What: repeats array values (`key=A&key=B`) instead of axios' `key[]=`. Empty values are dropped.
 * Plain strings (such as the `filter` JSON from `toCatalogFilterParam`) are appended once;
 * URLSearchParams percent-encodes the JSON, which the server decodes to the same value.
 * Why: `stationId`, `category` and `shelfLocation` now go inside the `filter` JSON. Other array
 * params (if any) still repeat as keys.
 * Constraint: do not pass a `filter` key from anywhere else in these requests; it is built by
 * `toCatalogFilterParam`.
 */
const serializeCatalogParams = (params: Record<string, TCatalogQueryValue>) => {
  const search = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === '') {
      return;
    }

    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item) {
          search.append(key, item);
        }
      });
      return;
    }

    search.append(key, String(value));
  });

  return search.toString();
};

const selectedValues = (value?: string | string[], extra?: string[]) => {
  const base = Array.isArray(value) ? value : [value];
  const values = [...base.filter((item): item is string => Boolean(item)), ...(extra ?? [])]
    .map((item) => item.trim())
    .filter(Boolean);

  return values.length ? [...new Set(values)] : undefined;
};

const delay = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }

    let timer: number | undefined;

    const onAbort = () => {
      window.clearTimeout(timer);
      reject(new DOMException('Aborted', 'AbortError'));
    };

    timer = window.setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);

    signal?.addEventListener('abort', onAbort, { once: true });
  });

const toCollectionResponse = (
  body: TCatalogListResponse,
  page: number,
  size: number,
): TGetCatalogItemsResponse => {
  const result = body.result ?? body.data ?? [];
  const total = body.totalHits ?? body.total ?? result.length;
  const offset = page * size;
  const hasMore = body.hasMore ?? offset + result.length < total;

  return {
    message: '',
    data: {
      result,
      total,
      totalHits: total,
      hasMore,
    },
  };
};

export const getCatalogItems: TGetCatalogItems = (params, signal) => {
  const page = params.page ?? 0;
  const size = params.size ?? 20;
  const filter = params.filter ?? {};

  const stationIds = selectedValues(filter.stationId);

  // No site and not "All sites" (a Products tab without its site): nothing to list.
  if (!stationIds && !filter.allSites) {
    return Promise.resolve({ data: EMPTY_LIST_RESPONSE }) as AxiosPromise<TGetCatalogItemsResponse>;
  }

  const categories = selectedValues(filter.category, filter.categories);
  const shelfLocations = selectedValues(filter.shelfLocation, filter.shelfLocations);

  return catalogService
    .get<TCatalogListResponse>('/items', {
      params: {
        ...toCatalogFilterParam(stationIds, categories, shelfLocations),
        limit: size,
        offset: page * size,
        ...(filter.isActive !== undefined ? { isActive: filter.isActive } : {}),
        ...(filter.search ? { search: filter.search } : {}),
        ...(filter.subcategory ? { subcategory: filter.subcategory } : {}),
        ...(filter.itemNumber ? { itemNumber: filter.itemNumber } : {}),
        ...(filter.upcFull ? { upcFull: filter.upcFull } : {}),
        ...(filter.costEffectiveFrom ? { costEffectiveFrom: filter.costEffectiveFrom } : {}),
        ...(filter.costEffectiveTo ? { costEffectiveTo: filter.costEffectiveTo } : {}),
        // itemNumber, vendorName, productDescription, retailPrice, unitCost,
        // grossMarginPercent, costEffectiveDate, retailPriceEffectiveDate, importedAt.
        // A blank sort is the server default: itemNumber,asc.
        sort: params.sort || 'itemNumber,asc',
      },
      paramsSerializer: serializeCatalogParams,
      signal,
    })
    .then((response) => ({
      ...response,
      data: toCollectionResponse(response.data, page, size),
    }));
};

export const getCatalogItem = (id: string, signal?: AbortSignal) =>
  catalogService.get<TCatalogItem>(`/items/${encodeURIComponent(id)}`, { signal });

export const createCatalogItem = (payload: TCreateCatalogItem) =>
  catalogService.post<TCatalogItem>('/items', payload);

export const patchCatalogItem = (id: string, patch: TPatchCatalogItem) =>
  catalogService.patch<TCatalogItem>(`/items/${encodeURIComponent(id)}`, patch);

export const importCatalog = (
  file: File,
  stationId: string,
): AxiosPromise<TCatalogImportSubmitResponse> => {
  const body = new FormData();
  body.append('file', file);

  return catalogService.post<TCatalogImportSubmitResponse>('/imports', body, {
    params: {
      stationId,
      deactivateMissing: false,
    },
  });
};

export const getCatalogImportStatus = (requestId: string, signal?: AbortSignal) =>
  catalogService.get<TCatalogImportStatus>(`/imports/${encodeURIComponent(requestId)}`, { signal });

/** Polls import status sequentially until COMPLETED or FAILED (one request at a time). */
export const waitForCatalogImport = async (
  requestId: string,
  options?: { intervalMs?: number; initialDelayMs?: number; signal?: AbortSignal },
): Promise<TCatalogImportStatus> => {
  const intervalMs = options?.intervalMs ?? POLL_INTERVAL_MS;
  const initialDelayMs = options?.initialDelayMs ?? 0;
  const { signal } = options ?? {};

  if (initialDelayMs > 0) {
    await delay(initialDelayMs, signal);
  }

  for (;;) {
    if (signal?.aborted) {
      // eslint-disable-next-line @typescript-eslint/no-throw-literal -- DOMException is a real error object; callers detect cancellation via its `AbortError` name, so it must not become a plain Error
      throw new DOMException('Aborted', 'AbortError');
    }

    const { data } = await getCatalogImportStatus(requestId);

    if (data.status !== 'RUNNING') {
      return data;
    }

    await delay(intervalMs, signal);
  }
};

/**
 * Starts ONE export for every given site.
 *
 * What: sites, categories and shelf locations go as `filter={"stationId":[...],"category":[...],"shelfLocation":[...]}`.
 * Only non-empty arrays are included. No site (All sites) sends no site in the filter.
 * Why: same combined-filter format as every other catalog request. Not used by `importCatalog`,
 * which keeps a single `stationId`.
 */
export const startCatalogExport = (params: TStartCatalogExportParams) => {
  const stationIds = selectedValues(params.stationId);
  const categories = selectedValues(params.category);
  const shelfLocations = selectedValues(params.shelfLocation);

  return catalogService.post<TCatalogExportSubmitResponse>('/exports', null, {
    params: {
      ...toCatalogFilterParam(stationIds, categories, shelfLocations),
      ...(params.isActive !== undefined ? { isActive: params.isActive } : {}),
      ...(params.subcategory ? { subcategory: params.subcategory } : {}),
      ...(params.itemNumber ? { itemNumber: params.itemNumber } : {}),
      ...(params.upcFull ? { upcFull: params.upcFull } : {}),
      ...(params.search ? { search: params.search } : {}),
      ...(params.costEffectiveFrom ? { costEffectiveFrom: params.costEffectiveFrom } : {}),
      ...(params.costEffectiveTo ? { costEffectiveTo: params.costEffectiveTo } : {}),
    },
    paramsSerializer: serializeCatalogParams,
  });
};

/**
 * Location / Category options across one or several sites, optionally cross-filtered.
 *
 * What: sites, categories and shelf locations go as `filter={"stationId":[...],"category":[...],"shelfLocation":[...]}`.
 * Only non-empty arrays are included. No arrays gives no `filter` param. Uses the catalog serializer
 * so the `filter` string is appended once.
 * Why: when the operator picks categories, the Location dropdown shows only locations that have
 * products in those categories (and vice versa). The backend reads all three from the same
 * `filter` JSON.
 */
export const getCatalogFilterOptions = (params: TCatalogFilterOptionsParams) => {
  const stationIds = selectedValues(params.stationId);
  const categories = params.category?.length ? params.category : undefined;
  const shelfLocations = params.shelfLocation?.length ? params.shelfLocation : undefined;

  return catalogService.get<TCatalogFilterOptionsResponse>('/filter-options', {
    params: {
      ...toCatalogFilterParam(stationIds, categories, shelfLocations),
      field: params.field,
      ...(params.q ? { q: params.q } : {}),
    },
    paramsSerializer: serializeCatalogParams,
    ...(params.signal ? { signal: params.signal } : {}),
  });
};

/**
 * Poll/download export file by requestId.
 * HTTP status maps to state: 202 RUNNING, 200 COMPLETED (xlsx blob), 404 FAILED.
 */
export const downloadCatalogExport = async (
  requestId: string,
  signal?: AbortSignal,
): Promise<TCatalogExportDownloadResult> => {
  const response = await catalogService.get<Blob>(`/exports/${encodeURIComponent(requestId)}`, {
    responseType: 'blob',
    validateStatus: (status) => status === 200 || status === 202 || status === 404,
    signal,
  });

  if (response.status === 202) {
    return { status: 'RUNNING' };
  }

  if (response.status === 200) {
    return { status: 'COMPLETED', blob: response.data };
  }

  return { status: 'FAILED' };
};

/** Polls export download sequentially until COMPLETED (blob) or FAILED (throws). */
export const waitForCatalogExportDownload = async (
  requestId: string,
  options?: { intervalMs?: number; signal?: AbortSignal },
): Promise<Blob> => {
  const intervalMs = options?.intervalMs ?? POLL_INTERVAL_MS;
  const { signal } = options ?? {};

  for (;;) {
    if (signal?.aborted) {
      // eslint-disable-next-line @typescript-eslint/no-throw-literal -- DOMException is a real error object; callers detect cancellation via its `AbortError` name, so it must not become a plain Error
      throw new DOMException('Aborted', 'AbortError');
    }

    const result = await downloadCatalogExport(requestId);

    if (result.status === 'COMPLETED') {
      return result.blob;
    }

    if (result.status === 'FAILED') {
      throw new Error('Catalog export failed or request expired');
    }

    await delay(intervalMs, signal);
  }
};
